import { usePhotoSkeleton } from "@/hooks/usePhotoSkeleton";
import { cn } from "@/lib/cn";
import type { ComponentProps } from "react";

/**
 * A lazy-loaded photo under a shimmer that fades out once the file arrives.
 * `className` sizes and places the frame; the image fills it.
 */
export function Photo({
  className,
  imgClassName,
  tone = "light",
  onLoad,
  ...props
}: ComponentProps<"img"> & {
  imgClassName?: string;
  tone?: "light" | "dark";
}) {
  const { imgProps, skeleton } = usePhotoSkeleton();
  return (
    <div
      className={cn("grid grid-cols-1 grid-rows-1 overflow-hidden", className)}
    >
      <img
        loading="lazy"
        decoding="async"
        {...props}
        ref={imgProps.ref}
        onLoad={(event) => {
          imgProps.onLoad();
          onLoad?.(event);
        }}
        className={cn(
          "col-start-1 row-start-1 size-full object-cover",
          imgClassName,
        )}
      />
      {skeleton && (
        <span
          aria-hidden
          onTransitionEnd={skeleton.onTransitionEnd}
          className={cn(
            "col-start-1 row-start-1",
            skeleton.className,
            tone === "dark" && "animate-skeleton-dark",
          )}
        />
      )}
    </div>
  );
}
